/**
 * IDBHistoryRepository — web implementation of HistoryRepository.
 *
 * Uses idb-keyval with a dedicated custom store ("BitDrop" db, "transfers" object store).
 * Never touches the default keyval-store — prevents key collisions with other app data.
 *
 * One IDB record per session.
 * Key: session.id (no prefix — the dedicated store provides isolation)
 *
 * revision guard: stale checkpoints with revision <= stored revision are discarded.
 * Uses idb-keyval `update()` for atomic read-modify-write (not get+set).
 */

import { createStore, get, set, del, entries, update } from 'idb-keyval';
import type { HistoryRepository, PersistedSession, ResumeData } from '@share/domain';
import { filterInterruptedSessions, filterPausedReceiveSessions } from './history-filters';


// Dedicated IDB object store — never shares with the default keyval-store
const transferStore = createStore('BitDrop', 'transfers');

export class IDBHistoryRepository implements HistoryRepository {

  async saveSession(session: PersistedSession): Promise<void> {
    // Atomic update: read current, check revision, write only if newer
    await update<PersistedSession>(session.id, (existing) => {
      if (existing && existing.revision >= session.revision) {
        return existing; // Stale checkpoint — discard silently
      }
      return session;
    }, transferStore);
  }

  async getSession(id: string): Promise<PersistedSession | null> {
    const record = await get<PersistedSession>(id, transferStore);
    return record ?? null;
  }

  async getAllSessions(): Promise<PersistedSession[]> {
    const allEntries = await entries<string, PersistedSession>(transferStore);
    return allEntries
      .map(([, session]) => session)
      .sort((a, b) => b.createdAt - a.createdAt);
  }

  async deleteSession(id: string): Promise<void> {
    await del(id, transferStore);
  }

  async updateResumeData(sessionId: string, resumeData: ResumeData): Promise<void> {
    // Atomic: only update the resumeData field — does not change revision
    // If the record doesn't exist, the update is a no-op (existing stays undefined)
    await update<PersistedSession>(
      sessionId,
      (existing) => {
        if (!existing) return existing as unknown as PersistedSession;
        return { ...existing, resumeData };
      },
      transferStore
    );
  }

  async getInterruptedSessions(): Promise<PersistedSession[]> {
    const all = await this.getAllSessions();
    return filterInterruptedSessions(all);
  }

  async getPausedReceiveSessions(): Promise<PersistedSession[]> {
    const all = await this.getAllSessions();
    return filterPausedReceiveSessions(all);
  }
}
